/**
 * API client.
 *
 * Carries the session token as a bearer header. The token is kept in
 * localStorage so a reload does not sign you out; it is signed and short-lived
 * server-side, and nothing else about the session lives in the browser.
 */

const BASE = import.meta.env.VITE_API_BASE || "/api"
const TOKEN_KEY = "loomina.token"

export class ApiError extends Error {
	constructor(status, code, message, detail) {
		super(message)
		this.status = status
		this.code = code
		this.detail = detail
	}
}

let token = null
let onUnauthorised = null

try {
	token = window.localStorage.getItem(TOKEN_KEY)
} catch {
	token = null
}

export function setToken(value) {
	token = value || null
	try {
		if (value) window.localStorage.setItem(TOKEN_KEY, value)
		else window.localStorage.removeItem(TOKEN_KEY)
	} catch {
		/* private browsing - the session still works for this tab */
	}
}

export function getToken() {
	return token
}

/** Called when the server rejects the token, so the app can bounce to sign-in. */
export function setUnauthorisedHandler(handler) {
	onUnauthorised = handler
}

async function request(method, path, { body, query, auth = true, raw = false } = {}) {
	const url = new URL(BASE + path, window.location.origin)
	Object.entries(query || {}).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== "") {
			url.searchParams.set(key, value)
		}
	})

	const headers = { Accept: raw ? "text/plain, application/json" : "application/json" }
	if (auth && token) headers.Authorization = `Bearer ${token}`
	if (body !== undefined) headers["Content-Type"] = "application/json"

	let response
	try {
		response = await fetch(url.toString().replace(window.location.origin, ""), {
			method,
			headers,
			body: body === undefined ? undefined : JSON.stringify(body)
		})
	} catch (cause) {
		throw new ApiError(0, "offline", "Could not reach the Loomina API.", cause)
	}

	if (response.status === 204) return null

	const text = await response.text()
	// `raw` asks for the body as text - a file preview, not a JSON reply. Errors
	// still come back as JSON, so only a successful answer skips the parse.
	if (raw && response.ok) return text

	let payload = null
	if (text) {
		try {
			payload = JSON.parse(text)
		} catch {
			payload = null
		}
	}

	if (!response.ok) {
		const error = payload?.error || {}
		if (response.status === 401 && auth) {
			setToken(null)
			onUnauthorised?.()
		}
		throw new ApiError(
			response.status,
			error.code || "error",
			error.message || `Request failed (${response.status})`,
			error.detail
		)
	}

	return payload
}

export const api = {
	/* ---- public ---------------------------------------------------------- */
	health: () => request("GET", "/health", { auth: false }),
	config: () => request("GET", "/config", { auth: false }),

	/* ---- auth ------------------------------------------------------------ */
	register: (body) => request("POST", "/auth/register", { body, auth: false }),
	signIn: (body) => request("POST", "/auth/sign-in", { body, auth: false }),
	verify: (body) => request("POST", "/auth/verify", { body, auth: false }),
	resendCode: (challengeId) =>
		request("POST", "/auth/resend-code", { body: { challenge_id: challengeId }, auth: false }),
	forgotPassword: (email) =>
		request("POST", "/auth/forgot", { body: { email }, auth: false }),
	resetPassword: (body) => request("POST", "/auth/reset", { body, auth: false }),
	me: () => request("GET", "/auth/me"),
	updateMe: (body) => request("PATCH", "/auth/me", { body }),
	changePassword: (body) => request("POST", "/auth/password", { body }),
	oauthStartUrl: (provider, next = "/") =>
		`${BASE}/auth/oauth/${provider}/start?next=${encodeURIComponent(next)}`,

	/* ---- directory ------------------------------------------------------- */
	people: () => request("GET", "/people"),

	/* ---- projects -------------------------------------------------------- */
	projects: () => request("GET", "/projects"),
	project: (projectId) => request("GET", `/projects/${projectId}`),
	createProject: (body) => request("POST", "/projects", { body }),
	updateProject: (projectId, body) => request("PATCH", `/projects/${projectId}`, { body }),
	deleteProject: (projectId) => request("DELETE", `/projects/${projectId}`),
	addProjectTeam: (projectId, teamId) =>
		request("POST", `/projects/${projectId}/teams`, { body: { team_id: teamId } }),
	removeProjectTeam: (projectId, teamId) =>
		request("DELETE", `/projects/${projectId}/teams/${teamId}`),

	/* ---- issues ---------------------------------------------------------- */
	issues: (projectId, query) => request("GET", `/projects/${projectId}/issues`, { query }),
	issue: (projectId, issueId) => request("GET", `/projects/${projectId}/issues/${issueId}`),
	createIssue: (projectId, body) => request("POST", `/projects/${projectId}/issues`, { body }),
	updateIssue: (projectId, issueId, body) =>
		request("PATCH", `/projects/${projectId}/issues/${issueId}`, { body }),
	setIssueStatus: (projectId, issueId, status) =>
		request("POST", `/projects/${projectId}/issues/${issueId}/status`, {
			body: { status }
		}),
	assignIssue: (projectId, issueId, body) =>
		request("POST", `/projects/${projectId}/issues/${issueId}/assign`, { body }),
	addMembers: (projectId, issueId, body) =>
		request("POST", `/projects/${projectId}/issues/${issueId}/members`, { body }),
	removeMember: (projectId, issueId, userId) =>
		request("DELETE", `/projects/${projectId}/issues/${issueId}/members/${userId}`),

	addComment: (projectId, issueId, body) =>
		request("POST", `/projects/${projectId}/issues/${issueId}/comments`, { body }),
	updateComment: (projectId, issueId, commentId, body) =>
		request("PATCH", `/projects/${projectId}/issues/${issueId}/comments/${commentId}`, {
			body
		}),

	/* ---- tags ------------------------------------------------------------ */
	tags: (projectId) => request("GET", `/projects/${projectId}/tags`),
	createTag: (projectId, body) => request("POST", `/projects/${projectId}/tags`, { body }),
	updateTag: (projectId, tagId, body) =>
		request("PATCH", `/projects/${projectId}/tags/${tagId}`, { body }),
	deleteTag: (projectId, tagId) => request("DELETE", `/projects/${projectId}/tags/${tagId}`),

	/* ---- teams ----------------------------------------------------------- */
	teams: () => request("GET", "/teams"),
	createTeam: (body) => request("POST", "/teams", { body }),
	updateTeam: (teamId, body) => request("PATCH", `/teams/${teamId}`, { body }),
	removeTeamMember: (teamId, userId) => request("DELETE", `/teams/${teamId}/members/${userId}`),
	removeTeamPending: (teamId, email) =>
		request("POST", `/teams/${teamId}/pending/remove`, { body: { email } }),
	deleteTeam: (teamId) => request("DELETE", `/teams/${teamId}`),

	/* ---- attachments ----------------------------------------------------- */
	// Base64-in-JSON. Kept for compatibility only: the request body is capped
	// well below most plans' file limits, so the editor uses `uploadFile`.
	upload: (projectId, body) => request("POST", `/projects/${projectId}/attachments`, { body }),
	startUpload: (projectId, body) =>
		request("POST", `/projects/${projectId}/attachments/uploads`, { body }),
	completeUpload: (projectId, uploadId) =>
		request("POST", `/projects/${projectId}/attachments/uploads/${uploadId}/complete`),
	attachmentText: (attachmentId) =>
		request("GET", `/attachments/${attachmentId}`, { query: { as: "text" }, raw: true }),

	/* ---- API tokens ------------------------------------------------------ */
	apiTokens: () => request("GET", "/auth/tokens"),
	createApiToken: (body) => request("POST", "/auth/tokens", { body }),
	revokeApiToken: (tokenId) => request("DELETE", `/auth/tokens/${tokenId}`),
	setApiTokenSuspended: (tokenId, suspended) =>
		request("POST", `/auth/tokens/${tokenId}/${suspended ? "suspend" : "resume"}`),

	/* ---- dashboards ------------------------------------------------------ */
	dashboard: (days) => request("GET", "/dashboard", { query: { days } }),
	projectDashboard: (projectId, days) =>
		request("GET", `/projects/${projectId}/dashboard`, { query: { days } }),

	/* ---- share ----------------------------------------------------------- */
	exportProject: (projectId, status) =>
		request("GET", `/projects/${projectId}/export`, { query: { status } }),
	exportIssue: (projectId, issueId) =>
		request("GET", `/projects/${projectId}/issues/${issueId}/export`),

	/* ---- billing --------------------------------------------------------- */
	billing: () => request("GET", "/billing"),
	checkout: (plan, interval) =>
		request("POST", "/billing/checkout", { body: { plan, interval } }),
	billingPortal: () => request("POST", "/billing/portal")
}

/**
 * Attachments are fetched by the browser directly (an <img src>), which cannot
 * carry an Authorization header - so authenticated media URLs carry the token
 * as a query parameter instead. The server accepts either.
 */
export function mediaUrl(url) {
	if (!url || !token) return url
	const separator = url.includes("?") ? "&" : "?"
	return `${url}${separator}token=${encodeURIComponent(token)}`
}

/** The API's absolute address, for commands pasted somewhere outside the app. */
export function apiOrigin() {
	return new URL(BASE, window.location.origin).toString().replace(/\/$/, "")
}

/**
 * Upload one file straight to storage, in three steps.
 *
 * The server hands back a short-lived link, the browser PUTs the raw bytes to
 * it, and the server then registers what arrived. The link authorises itself -
 * it may point at S3 rather than at us - so it is sent exactly the headers the
 * server named and never the session token.
 */
export async function uploadFile(projectId, file, issueId) {
	const started = await api.startUpload(projectId, {
		filename: file.name,
		content_type: file.type || "application/octet-stream",
		size_bytes: file.size,
		issue_id: issueId
	})

	let response
	try {
		response = await fetch(started.url, {
			method: started.method || "PUT",
			headers: started.headers || {},
			body: file
		})
	} catch (cause) {
		throw new ApiError(0, "upload_failed", `Could not upload ${file.name}.`, cause)
	}
	if (!response.ok) {
		throw new ApiError(
			response.status,
			"upload_failed",
			`Could not upload ${file.name} (${response.status}).`
		)
	}

	return api.completeUpload(projectId, started.upload_id)
}

/** Bodies over this are refused by the server with `body_too_long`. */
export const BODY_LIMIT = 50_000

/** An error's message, reworded where the server's own would not help. */
export function errorMessage(cause) {
	if (cause?.code === "body_too_long") {
		const limit = cause.detail?.limit ?? BODY_LIMIT
		const length = cause.detail?.length
		return (
			`That is too long to save${
				length ? ` (${length.toLocaleString()} characters)` : ""
			} - the limit is ${limit.toLocaleString()}. ` +
			"Trim it, or attach the rest as a file."
		)
	}
	return cause?.message || "Something went wrong."
}

/* ---- media tokens in stored bodies -------------------------------------- */

const ATTACHMENT_PATH = /\/attachments\//

/** Drop a `token` query parameter, keeping the URL otherwise as written. */
function withoutToken(url) {
	const at = url.indexOf("?")
	if (at === -1) return url
	const params = new URLSearchParams(url.slice(at + 1))
	if (!params.has("token")) return url
	params.delete("token")
	const query = params.toString()
	return query ? `${url.slice(0, at)}?${query}` : url.slice(0, at)
}

function rewriteImages(html, rewrite) {
	if (!html || !ATTACHMENT_PATH.test(html)) return html || ""
	// A <template> parses without loading anything, so no image is fetched
	// with the old URL on the way through.
	const scratch = document.createElement("template")
	scratch.innerHTML = html
	scratch.content.querySelectorAll("img[src]").forEach((img) => {
		const src = img.getAttribute("src")
		if (ATTACHMENT_PATH.test(src)) img.setAttribute("src", rewrite(withoutToken(src)))
	})
	return scratch.innerHTML
}

/**
 * Bodies store attachment URLs bare. The token goes on only when the body is
 * shown, and it is the viewer's own - a token saved into a body would hand the
 * author's session to everyone who can read the thread.
 */
export function withMediaTokens(html) {
	return rewriteImages(html, mediaUrl)
}

/** The inverse, for anything about to be saved. Also cleans older bodies. */
export function stripMediaTokens(html) {
	return rewriteImages(html, (url) => url)
}
